import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Paper,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, PhotoCamera, HelpOutline } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';

const Dashboard = () => {
  const [loras, setLoras] = useState([]);
  const [openNewLora, setOpenNewLora] = useState(false);
  const [newLoraName, setNewLoraName] = useState('');
  const [errors, setErrors] = useState({});
  const [loraImages, setLoraImages] = useState({});
  const [uploading, setUploading] = useState({});
  const [training, setTraining] = useState({});
  const [trainingStatus, setTrainingStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user_email = localStorage.getItem('user_email');
    
    if (!token || !user_email) {
      navigate('/login');
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['x-email'] = user_email;
    axios.defaults.timeout = 30000; // Set default timeout to 30 seconds
    
    fetchLoras();
  }, [navigate]);

  useEffect(() => {
    let intervalId;
    
    const checkTrainingStatus = async () => {
      const trainingLoras = loras.filter(lora => lora.status === 'training');
      
      if (trainingLoras.length === 0) {
        return;
      }

      try {
        for (const lora of trainingLoras) {
          try {
            const response = await axios.get(`/api/loras/${lora._id}/training-status`);
            if (response.data.status === 'completed' || response.data.status === 'failed') {
              await fetchLoras();
            }
            setTrainingStatus(prev => ({
              ...prev,
              [lora._id]: response.data
            }));
          } catch (error) {
            console.error(`Error checking training status for LoRA ${lora._id}:`, error);
          }
        }
      } catch (error) {
        console.error('Error in checkTrainingStatus:', error);
      }
    };

    if (loras.some(lora => lora.status === 'training')) {
      intervalId = setInterval(checkTrainingStatus, 30000); // Check every 30 seconds
      checkTrainingStatus(); // Check immediately
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loras]);

  const fetchLoras = async () => {
    try {
      const response = await axios.get('/api/loras', {
        timeout: 10000 // 10 second timeout for this specific request
      });
      setLoras(response.data);
      response.data.forEach(lora => {
        fetchLoraImages(lora._id);
      });
    } catch (error) {
      console.error('Error fetching loras:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const fetchLoraImages = async (loraId) => {
    try {
      const response = await axios.get(`/api/loras/${loraId}/images`);
      setLoraImages(prev => ({
        ...prev,
        [loraId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleCreateLora = async () => {
    try {
      await axios.post('/api/loras', { name: newLoraName });
      setOpenNewLora(false);
      setNewLoraName('');
      fetchLoras();
    } catch (error) {
      console.error('Error creating lora:', error);
      setErrors(prev => ({
        ...prev,
        [newLoraName]: error.response?.data?.error || 'Failed to create LoRA'
      }));
    }
  };

  const handleDeleteImage = async (loraId, imageId) => {
    try {
      await axios.delete(`/api/loras/${loraId}/images/${imageId}`);
      fetchLoraImages(loraId);
    } catch (error) {
      console.error('Error deleting image:', error);
      setErrors(prev => ({
        ...prev,
        [loraId]: 'Failed to delete image'
      }));
    }
  };

  const handleStartTraining = async (loraId) => {
    try {
      setTraining(prev => ({ ...prev, [loraId]: true }));
      setErrors(prev => ({ ...prev, [loraId]: null }));

      const images = loraImages[loraId];
      if (!images || images.length < 5) {
        throw new Error('Please upload 5 images before training');
      }

      // Extract image URLs and ensure they're valid
      const imageUrls = images.map(img => {
        if (!img.url) {
          throw new Error('Invalid image data');
        }
        return img.url;
      });

      const response = await axios.post(`/api/loras/${loraId}/train`, {
        images: imageUrls
      });

      await fetchLoras();
    } catch (error) {
      console.error('Training error:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to start training';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.data.details) {
          console.error('Error details:', error.response.data.details);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors(prev => ({
        ...prev,
        [loraId]: errorMessage
      }));
    } finally {
      setTraining(prev => ({ ...prev, [loraId]: false }));
    }
  };

  const handleUpload = async (loraId, files) => {
    setUploading(prev => ({ ...prev, [loraId]: true }));
    setErrors(prev => ({ ...prev, [loraId]: null }));

    try {
      const currentImages = loraImages[loraId] || [];
      const remainingSlots = 12 - currentImages.length;
      const filesToUpload = files.slice(0, remainingSlots);

      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `/api/loras/${loraId}/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data) {
          await fetchLoraImages(loraId);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setErrors(prev => ({
        ...prev,
        [loraId]: error.response?.data?.error || 'Failed to upload images'
      }));
    } finally {
      setUploading(prev => ({ ...prev, [loraId]: false }));
    }
  };

  const handleDeleteLora = async (loraId) => {
    try {
      await axios.delete(`/api/loras/${loraId}`);
      fetchLoras();
    } catch (error) {
      console.error('Error deleting LoRA:', error);
      setErrors(prev => ({
        ...prev,
        [loraId]: 'Failed to delete LoRA'
      }));
    }
  };

  const getTrainingStage = (progress) => {
    if (!progress) return null;
    
    // Stage 1: Preparing images (0-5%)
    if (progress.percentage <= 5) {
      return {
        stage: 1,
        total_stages: 3,
        name: 'Preparing Images',
        description: 'Extracting and processing your training images'
      };
    }
    
    // Stage 2: Model initialization (5-15%)
    if (progress.percentage <= 15) {
      return {
        stage: 2,
        total_stages: 3,
        name: 'Initializing Model',
        description: 'Setting up the AI model with your images'
      };
    }
    
    // Stage 3: Training (15-100%)
    return {
      stage: 3,
      total_stages: 3,
      name: 'Training Model',
      description: 'Teaching the AI to generate images in your style'
    };
  };

  const UploadRow = ({ loraId, images = [] }) => {
    const isUploading = uploading[loraId];
    const isTraining = training[loraId];
    const currentLora = loras.find(l => l._id === loraId);
    const isCompleted = currentLora?.status === 'completed';
    const isTrainingOrCompleted = currentLora?.status === 'training' || isCompleted;
    const hasMinimumImages = (images?.length || 0) >= 5;
    const remainingSlots = 12 - (images?.length || 0);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'image/*': ['.jpeg', '.jpg', '.png']
      },
      maxSize: 10485760, // 10MB
      maxFiles: remainingSlots,
      multiple: true,
      disabled: isUploading || isTrainingOrCompleted,
      onDropRejected: (fileRejections) => {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === 'too-many-files') {
          setErrors(prev => ({
            ...prev,
            [loraId]: `You can only upload ${remainingSlots} more image${remainingSlots === 1 ? '' : 's'}`
          }));
        } else if (error?.code === 'file-too-large') {
          setErrors(prev => ({
            ...prev,
            [loraId]: 'Each image must be less than 10MB'
          }));
        }
      },
      onDrop: (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          handleUpload(loraId, acceptedFiles);
        }
      }
    });

    return (
      <>
        {!isTrainingOrCompleted && (
          <Box
            {...getRootProps()}
            sx={{
              mt: 2,
              position: 'relative',
              cursor: remainingSlots > 0 ? 'pointer' : 'default',
              minHeight: '200px',
              border: '2px dashed',
              borderColor: isDragActive ? '#369FA1' : '#ccc',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDragActive ? 'rgba(54, 159, 161, 0.1)' : 'transparent',
              transition: 'all 0.3s ease',
              '&:hover': remainingSlots > 0 ? {
                borderColor: '#369FA1',
                bgcolor: 'rgba(54, 159, 161, 0.1)'
              } : {}
            }}
          >
            <input {...getInputProps()} />
            
            <PhotoCamera sx={{ color: '#ccc', fontSize: 40, mb: 2 }} />
            <Typography variant="body1" color="textSecondary" align="center">
              {isDragActive 
                ? 'Drop your images here'
                : 'Drag and drop your images here, or click to select files'}
            </Typography>
            <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 1 }}>
              {images?.length || 0} of 12 images uploaded (minimum 5 required)
            </Typography>

            {isUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircularProgress size={40} sx={{ color: '#369FA1', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  Uploading images...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Display uploaded images */}
        {images && images.length > 0 && (
          <Box sx={{ mt: isTrainingOrCompleted ? 0 : 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {isTrainingOrCompleted ? 'Training Images' : 'Uploaded Images'}
            </Typography>
            <Grid container spacing={2}>
              {images.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={image._id}>
                  <Paper
                    sx={{
                      position: 'relative',
                      paddingTop: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: 1
                    }}
                  >
                    <img
                      src={image.url}
                      alt={`Training image ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {!isTrainingOrCompleted && (
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 5,
                          right: 5,
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.7)'
                          }
                        }}
                        onClick={() => handleDeleteImage(loraId, image._id)}
                      >
                        <DeleteIcon sx={{ color: 'white' }} />
                      </IconButton>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {errors[loraId] && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {errors[loraId]}
            </Alert>
          )}

          {hasMinimumImages && !isTrainingOrCompleted && (
            <Button
              variant="contained"
              disabled={isTraining}
              onClick={() => handleStartTraining(loraId)}
              startIcon={isTraining ? <CircularProgress size={20} /> : null}
              sx={{
                bgcolor: '#369FA1',
                '&:hover': {
                  bgcolor: '#2B8183'
                }
              }}
            >
              {isTraining ? 'Training...' : 'Train LoRA'}
            </Button>
          )}

          {isCompleted && (
            <Button
              variant="contained"
              onClick={() => navigate(`/generate/${loraId}`)}
              sx={{
                bgcolor: '#369FA1',
                '&:hover': { bgcolor: '#2B8183' }
              }}
            >
              Create images with my avatar
            </Button>
          )}

          {!isTrainingOrCompleted && !hasMinimumImages && (
            <Typography 
              variant="body2" 
              color="textSecondary" 
              align="center"
            >
              Upload at least {5 - (images?.length || 0)} more image{5 - (images?.length || 0) === 1 ? '' : 's'} to start training
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#369FA1' }}>
          My LoRAs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/all-generated-images')}
            sx={{
              color: '#369FA1',
              borderColor: '#369FA1',
              '&:hover': {
                borderColor: '#2B8183',
                bgcolor: 'rgba(54, 159, 161, 0.1)'
              }
            }}
          >
            View All Generated Images
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenNewLora(true)}
            sx={{
              bgcolor: '#369FA1',
              '&:hover': {
                bgcolor: '#2B8183'
              }
            }}
          >
            New LoRA
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {loras.map((lora) => (
          <Grid item xs={12} key={lora._id}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2">
                      {lora.name}
                    </Typography>
                    <Typography color="textSecondary">
                      Status: {lora.status}
                    </Typography>
                    {lora.status === 'training' && trainingStatus[lora._id]?.training_progress && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">Training Progress... Please wait.</Typography>
                        {(() => {
                          const stage = getTrainingStage(trainingStatus[lora._id].training_progress);
                          return stage && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" color="primary" fontWeight="medium">
                                Stage {stage.stage} of {stage.total_stages}: {stage.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {stage.description}
                              </Typography>
                            </Box>
                          );
                        })()}
                        <LinearProgress 
                          variant="determinate" 
                          value={Number(trainingStatus[lora._id].training_progress.percentage) || 0}
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          Overall Progress: {trainingStatus[lora._id].training_progress.percentage}%
                        </Typography>
                        {trainingStatus[lora._id].training_progress.loss && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="textSecondary">
                              Training Quality (Loss): {Number(trainingStatus[lora._id].training_progress.loss).toFixed(4)}
                            </Typography>
                            <Tooltip title="Loss indicates how well the AI is learning. A lower number means better learning. It should decrease over time during training." placement="right">
                              <IconButton size="small">
                                <HelpOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          Last Update: {new Date(trainingStatus[lora._id].training_progress.last_update).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    onClick={() => handleDeleteLora(lora._id)}
                    sx={{ color: '#d32f2f' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <UploadRow
                  loraId={lora._id}
                  images={loraImages[lora._id]}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openNewLora} onClose={() => setOpenNewLora(false)}>
        <DialogTitle>Create New LoRA</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="LoRA Name"
            fullWidth
            value={newLoraName}
            onChange={(e) => setNewLoraName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewLora(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateLora} 
            variant="contained"
            sx={{
              bgcolor: '#369FA1',
              '&:hover': {
                bgcolor: '#2B8183'
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard; 